import { createRejectOnErrorStatus, parseErrorMessage, transformWithErrorBody } from './httpResponse';

const alarmServices = angular.module('alarmServices', []);

const isSuccessStatus = status => status >= 200 && status < 300;

alarmServices.service('alarmRuleService', function ($resource, $http, $q, config) {
  const wrapErrorBody = transformWithErrorBody($http.defaults.transformResponse);
  const rejectOnErrorStatus = createRejectOnErrorStatus($q);

  const rest = $resource(
    config.api + 'alarm-rule',
    {},
    {
      list: {
        url: config.api + 'alarm-rule/list',
        method: 'GET',
        isArray: true,
      },
      get: {
        url: config.api + 'alarm-rule/:id',
        method: 'GET',
      },
      create: {
        url: config.api + 'alarm-rule',
        method: 'POST',
        // Endpoint returns a bare id; $resource cannot copy a primitive into a Resource.
        transformResponse: (data, headers, status) =>
          (isSuccessStatus(status) ? { id: Number(data) } : { errorMessage: parseErrorMessage(data), httpStatus: status }),
      },
      // Editing is versioned server-side: a new version is created and the source rule archived.
      edit: {
        url: config.api + 'alarm-rule/:id/edit',
        method: 'POST',
        transformResponse: wrapErrorBody,
      },
      archive: {
        url: config.api + 'alarm-rule/:id/archive',
        method: 'PATCH',
        transformResponse: wrapErrorBody,
      },
      unarchive: {
        url: config.api + 'alarm-rule/:id/unarchive',
        method: 'PATCH',
        transformResponse: wrapErrorBody,
      },
      setActive: {
        url: config.api + 'alarm-rule/:id/active',
        method: 'PATCH',
        transformResponse: wrapErrorBody,
      },
      // In-place owner change; unlike edit, this does not version the rule// legacy.
      // updateOwner: {
      //   url: config.api + 'alarm-rule/:id/owner',
      //   method: 'PATCH',
      // },
      updateOwnerText: {
        url: config.api + 'alarm-rule/:id/owner-text',
        method: 'PATCH',
      },
      subscribe: {
        url: config.api + 'alarm-rule/:id/subscription',
        method: 'PATCH',
        transformResponse: wrapErrorBody,
      },
      triggerRunner: {
        url: config.api + 'alarm-rule-runner/generate',
        method: 'POST',
      },
    }
  );

  const getSummary = id => {
    if (id == null || !/^\d+$/.test(String(id))) {
      return $q.reject(new Error('Invalid rule id'));
    }
    return $http.get(`${config.api}alarm-rule/${id}/summary`, {
      transformResponse: wrapErrorBody,
    }).then(rejectOnErrorStatus).then(response => {
      if (!isSuccessStatus(response.status)) return $q.reject(response);
      const summary = response.data;
      if (!summary || typeof summary !== 'object' || Array.isArray(summary) || String(summary.id) !== String(id)) {
        return $q.reject(new Error('Invalid rule summary response'));
      }
      return summary;
    });
  };

  const getRuleRuns = (ruleId, { count = 100, skip = 0, after } = {}) => {
    if (ruleId == null || !/^\d+$/.test(String(ruleId))) {
      return $q.reject(new Error('Invalid rule id'));
    }
    return $http.get(`${config.api}alarm-rule-run/rule/${ruleId}`, {
      params: { count, skip, after },
    }).then(response => {
      if (!isSuccessStatus(response.status)) return $q.reject(response);
      const result = response.data;
      if (!Array.isArray(result?.items) || !Number.isInteger(result.count) || result.count < 0
          || result.items.some(item => item?.id == null)) {
        return $q.reject(new Error('Invalid rule run response'));
      }
      return result;
    });
  };

  return {
    list: (showArchived = false) => rest.list({ showArchived }).$promise,
    get: id => rest.get({ id }).$promise,
    getSummary,
    getRuleRuns,
    listRuleTypes: () => $http.get(config.api + 'rule-type/list', {
      transformResponse: wrapErrorBody,
    }).then(rejectOnErrorStatus).then(response => {
      if (!isSuccessStatus(response.status)) return $q.reject(response);
      const types = response.data;
      if (!Array.isArray(types) || types.some(type =>
        !Number.isInteger(type?.id) || typeof type.name !== 'string' || !type.name.trim())) {
        return $q.reject(new Error('Invalid rule type response'));
      }
      return types;
    }),
    create: payload => rest.create({}, payload).$promise.then(rejectOnErrorStatus),
    edit: (id, payload) => rest.edit({ id }, payload).$promise.then(rejectOnErrorStatus),
    archive: id => rest.archive({ id }, {}).$promise.then(rejectOnErrorStatus),
    unarchive: id => rest.unarchive({ id }, {}).$promise.then(rejectOnErrorStatus),
    setActive: (id, isActive) => rest.setActive({ id }, { isActive }).$promise.then(rejectOnErrorStatus),
    //updateOwner: (id, ownerId) => rest.updateOwner({ id }, { ownerId }).$promise, //legacy
    updateOwnerText: (id, ownerText) => rest.updateOwnerText({ id }, { ownerText }).$promise,
    subscribe: (id, userId, subscribed) => rest.subscribe({ id }, { userId, subscribed }).$promise.then(rejectOnErrorStatus),
    updateSubscriptionsBatch: (id, subscriptions) => $http.patch(`${config.api}alarm-rule/${id}/subscriptions/users`, subscriptions, {
      transformResponse: wrapErrorBody,
    }).then(rejectOnErrorStatus).then(response => response.data),
    triggerRunner: () => rest.triggerRunner({}, {}).$promise,
  };
});

alarmServices.service('alarmEventService', function ($resource, $http, $q, config) {
  const wrapErrorBody = transformWithErrorBody($http.defaults.transformResponse);
  const rejectOnErrorStatus = createRejectOnErrorStatus($q);

  const rest = $resource(
    config.api + 'alarm-event',
    {},
    {
      list: {
        url: config.api + 'alarm-event/list',
        method: 'GET',
        isArray: true,
      },
      get: {
        url: config.api + 'alarm-event/:id',
        method: 'GET',
        transformResponse: data => ({ data: data ? JSON.parse(data) : null }),
      },
      getSummary: {
        url: config.api + 'alarm-event/:id/summary',
        method: 'GET',
        transformResponse: data => ({ data: typeof data === 'string' ? JSON.parse(data || 'null') : data }),
      },
      getOccurrences: {
        url: config.api + 'alarm-event/:id/occurrences',
        method: 'GET',
      },
      getOccurrenceEvents: {
        url: config.api + 'alarm-event/:id/occurrences/:occurrenceIndex/events',
        method: 'GET',
      },
      markHandled: {
        url: config.api + 'alarm-event/:id/handled',
        method: 'PATCH',
        transformResponse: wrapErrorBody,
      },
    }
  );

  return {
    list: (showArchived = false) => $http.get(`${config.api}alarm-event/list`, {
      params: { showArchived }, transformResponse: wrapErrorBody,
    }).then(rejectOnErrorStatus).then(response => response.data),
    get: id => rest.get({ id }).$promise.then(response => response.data),
    getSummary: id => $http.get(`${config.api}alarm-event/${id}/summary`, { transformResponse: wrapErrorBody })
      .then(rejectOnErrorStatus).then(response => response.data),
    getOccurrences: (id, { consist, count = 100, skip = 0 } = {}) =>
      $http.get(`${config.api}alarm-event/${id}/occurrences`, {
        params: { consist, count, skip }, transformResponse: wrapErrorBody,
      }).then(rejectOnErrorStatus).then(response => response.data),
    getOccurrenceEvents: (id, occurrenceIndex, { count = 100, skip = 0 } = {}) =>
      $http.get(`${config.api}alarm-event/${id}/occurrences/${occurrenceIndex}/events`, {
        params: { count, skip }, transformResponse: wrapErrorBody,
      }).then(rejectOnErrorStatus).then(response => response.data),
    markHandled: (id, handled = true) => rest.markHandled({ id }, { handled }).$promise.then(rejectOnErrorStatus),
  };
});

alarmServices.service('alarmRuleReportService', function ($http, $q, Blob, FileSaver, toasterService, config) {
  const wrapErrorBody = transformWithErrorBody($http.defaults.transformResponse);
  const rejectOnErrorStatus = createRejectOnErrorStatus($q);
  const exportUrl = config.api + 'alarm-rule-runner/export-run-as-report';
  const ruleExportUrl = ruleId => `${exportUrl}/${ruleId}`;

  const pagedReportUrl = config.api + 'alarm-rule-runner/run-as-report-paged';
  const reportRunUrl = reportRunId => `${config.api}alarm-rule-runner/report-run/${reportRunId}`;

  const getReportRunConsists = reportRunId =>
    $http.get(`${reportRunUrl(reportRunId)}/consists`, { transformResponse: wrapErrorBody })
      .then(rejectOnErrorStatus).then(response => response.data);

  const getReportRunOccurrences = (reportRunId, { consist, count = 100, skip = 0 } = {}) =>
    $http.get(`${reportRunUrl(reportRunId)}/occurrences`, { params: { consist, count, skip }, transformResponse: wrapErrorBody })
      .then(rejectOnErrorStatus).then(response => response.data);

  const getReportRunOccurrenceEvents = (reportRunId, occurrenceIndex, { count = 100, skip = 0 } = {}) =>
    $http.get(`${reportRunUrl(reportRunId)}/occurrences/${occurrenceIndex}/events`, { params: { count, skip }, transformResponse: wrapErrorBody })
      .then(rejectOnErrorStatus).then(response => response.data);

  const preparePagedReport = async response => {
    const report = response.data;
    if (report?.reportRunId == null) throw new Error('Invalid report run response');
    const consists = await getReportRunConsists(report.reportRunId);
    if (!Array.isArray(consists) || consists.some(consist => typeof consist !== 'string')) {
      throw new Error('Invalid report consist response');
    }
    return { ...report, id: report.reportRunId, uniqueConsistList: consists };
  };

  const runAsReportPaged = payload => $http.post(pagedReportUrl, payload, { transformResponse: wrapErrorBody })
    .then(rejectOnErrorStatus).then(preparePagedReport);

  const runRuleAsReportPaged = (ruleId, payload) =>
    $http.post(`${pagedReportUrl}/${ruleId}`, payload, { transformResponse: wrapErrorBody })
      .then(rejectOnErrorStatus).then(preparePagedReport);

  const postExport = (url, payload, type, filenamePrefix) => {
    const normalizedType = type === 'XLSX' ? 'XLSX' : 'CSV';
    const isExcel = normalizedType === 'XLSX';
    const contentType = isExcel
      ? 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
      : 'text/csv';

    return $http
      .post(url, payload, {
        params: { type: normalizedType },
        responseType: 'arraybuffer',
        headers: {
          Accept: contentType,
        },
        transformResponse: function (data, headers, statusCode) {
          return {
            blob: new Blob([data], { type: contentType }),
            statusCode: statusCode,
          };
        },
      })
      .then(result => {
        if (result.data.statusCode === 200) {
          FileSaver.saveAs(result.data.blob, `${filenamePrefix}.${isExcel ? 'xlsx' : 'csv'}`);
        }
        if (result.data.statusCode !== 200) toasterService.error('export.export-failed', 'error-type.NO_DATA_FOR_REQUEST');
      });
  };

  const exportRunAsReport = (payload, type) => postExport(exportUrl, payload, type, 'alarm-rule-report');

  const exportRunAsReportCsv = payload => exportRunAsReport(payload, 'CSV');

  const exportRunAsReportExcel = payload => exportRunAsReport(payload, 'XLSX');

  const exportRuleReport = (ruleId, eventFilter, type) => postExport(ruleExportUrl(ruleId), eventFilter, type, `alarm-rule-${ruleId}-report`);

  const exportRuleReportCsv = (ruleId, eventFilter) => exportRuleReport(ruleId, eventFilter, 'CSV');

  const exportRuleReportExcel = (ruleId, eventFilter) => exportRuleReport(ruleId, eventFilter, 'XLSX');

  return {
    runAsReportPaged: runAsReportPaged,
    runRuleAsReportPaged: runRuleAsReportPaged,
    getReportRunConsists: getReportRunConsists,
    getReportRunOccurrences: getReportRunOccurrences,
    getReportRunOccurrenceEvents: getReportRunOccurrenceEvents,
    exportRunAsReportCsv: exportRunAsReportCsv,
    exportRunAsReportExcel: exportRunAsReportExcel,
    exportRuleReportCsv: exportRuleReportCsv,
    exportRuleReportExcel: exportRuleReportExcel,
  };
});
